import { categorizeRisk, overlayToRiskCategory } from "../arch-overlay";
import type {
  ArchitectureData,
  Component,
  Container,
  ContainerKind,
  ExternalSystem,
  ExternalSystemKind,
  Risk,
  RiskCategory,
} from "./types";

// ---------------------------------------------------------------------------
// Legacy v0.1/v0.2 → canonical v0.3 mapping
//
// `parseV02` produces `ArchitectureData` with deprecated fields populated
// (`container.type`, flat `risk: string[]`, decision `context`/`decision`/
// `consequences`, etc.). This mapper enriches the same object with canonical
// v0.3 fields so the renderer reads one shape regardless of which parser
// ran. Deprecated fields are NOT removed — re-uploading an old doc against
// the v0.3 prompt is the upgrade path, so legacy reads stay valid until then.
//
// Intent of the v0.2 → v0.3 reconciliation (spans more than this file):
//
//   1. Risks. Flat strings (`container.risk: string[]`, `concerns[]`) become
//      structured `Risk` objects here. Category is derived by running
//      `categorizeRisk` — the same keyword/prefix heuristic the old
//      per-render overlay used — at parse time. Unmatched strings fall
//      through to `"other"`. Severity defaults to `"info"`, response to
//      LEGACY_RISK_RESPONSE_TEXT, `legacy_origin: true` so the UI can
//      render them with reduced styling (slate, not red). Real
//      categorization/severity/response come from re-uploading against
//      the v0.3 prompt.
//
//   2. Edges. `container.connects_to[]` (v0.2, one-directional) and v0.3
//      `flow.from`/`flow.to` (bidirectional) are normalised to a single
//      `CanonicalEdge` list in `src/lib/arch-canonical-edges.ts`. Edge
//      direction is preserved as-authored; the two shapes fold into one
//      list deduped by `(source, target)`. `use-graph-elements` is the
//      only file in the renderer that knows v0.2 edges exist — this
//      mapper does not touch edges.
//
//   3. Decisions. v0.2 fields `context`/`decision`/`consequences` are kept
//      untouched on the architecture object and rendered by `DecisionCards`
//      below the canonical fields (`title`, `chosen`, `options_considered`,
//      `superseded_by`). On a v0.3 doc the v0.2 block is empty and
//      collapses; on a v0.2 doc the two render adjacent. The visual
//      redundancy is intentional and self-heals on re-upload — branching
//      the render on which mapper-populated which field would add a
//      conditional nobody will remember in six months.
//
// Known issues in the legacy v0.2 parser (intentionally not fixed —
// v0.2 is dead-end-on-re-upload):
//   - TODO: a multiline `connects_to:` list (one target per indented line)
//     is parsed as a single literal string instead of a string[]. The
//     resulting canonical edge has `to = "[postgres, file-storage, ...]"`
//     instead of N separate edges. Only matters until the doc is re-
//     uploaded under the v0.3 prompt. Found during Phase 10 smoke on
//     wolf-dms.
// ---------------------------------------------------------------------------

const LEGACY_RISK_RESPONSE_TEXT = "Legacy risk — response not specified in source document.";

// Map v0.2 ContainerType → v0.3 ContainerKind. Unknown values fall through
// to "other".
const CONTAINER_TYPE_TO_KIND: Record<string, ContainerKind> = {
  frontend: "frontend",
  backend: "service",
  api: "api",
  database: "database",
  cache: "cache",
  queue: "queue",
  worker: "worker",
  gateway: "gateway",
  storage: "storage",
  cdn: "storage",
  other: "other",
};

const EXTERNAL_KIND_VALUES: ReadonlySet<ExternalSystemKind> = new Set([
  "saas",
  "infrastructure",
  "integration",
  "data-source",
  "ai-service",
  "auth-provider",
  "payment-provider",
  "other",
]);

function legacyRisk(description: string): Risk {
  // Run the same keyword/prefix heuristic the old DetailPanel did per-render,
  // but at parse time so the overlay matchers can read RiskCategory directly.
  // Risks the heuristic can't classify fall through to "other".
  const overlay = categorizeRisk(description);
  const category: RiskCategory = overlay ? overlayToRiskCategory(overlay) : "other";
  return {
    category,
    severity: "info",
    description,
    response: { kind: "mitigation", text: LEGACY_RISK_RESPONSE_TEXT },
    legacy_origin: true,
  };
}

function concernAsRisk(statement: string, why: string | undefined): Risk {
  return {
    category: "operability",
    severity: "warning",
    description: statement,
    response: {
      kind: "mitigation",
      text: why && why.trim().length > 0 ? why : LEGACY_RISK_RESPONSE_TEXT,
    },
    legacy_origin: true,
  };
}

function mapContainer(c: Container): Container {
  const kind: ContainerKind = c.kind ?? CONTAINER_TYPE_TO_KIND[c.type] ?? "other";
  const tech_stack: string[] = c.tech_stack ?? c.technology ?? [];
  const risks: Risk[] = [
    ...(c.risk ?? []).map(legacyRisk),
    ...(c.concerns ?? []).map((cn) => concernAsRisk(cn.statement, cn.why)),
  ];
  return {
    ...c,
    kind,
    tech_stack,
    risks,
    assumptions: c.assumptions ?? [],
  };
}

function mapExternalSystem(s: ExternalSystem): ExternalSystem {
  const rawKind = (s.type ?? "").toLowerCase();
  const kind: ExternalSystemKind = EXTERNAL_KIND_VALUES.has(rawKind as ExternalSystemKind)
    ? (rawKind as ExternalSystemKind)
    : "other";
  const risks: Risk[] = (s.risk ?? []).map(legacyRisk);
  return {
    ...s,
    kind,
    risks,
    assumptions: s.assumptions ?? [],
  };
}

function mapComponent(comp: Component, containerId: string): Component {
  const tech_stack: string[] = comp.tech_stack ?? comp.technology ?? [];
  const risks: Risk[] = (comp.risk ?? []).map(legacyRisk);
  return {
    ...comp,
    container: comp.container ?? containerId,
    tech_stack,
    risks,
    assumptions: comp.assumptions ?? [],
  };
}

export function mapLegacyToCanonical(arch: ArchitectureData): ArchitectureData {
  const containers = arch.containers.map(mapContainer);
  // Lift components to a flat top-level collection while keeping them
  // nested under their container (the existing renderer still walks
  // `container.components`).
  const flatComponents: Component[] = [];
  for (const c of containers) {
    const mappedNested = (c.components ?? []).map((cmp) => mapComponent(cmp, c.id));
    c.components = mappedNested;
    flatComponents.push(...mappedNested);
  }
  return {
    ...arch,
    containers,
    components: flatComponents,
    externalSystems: arch.externalSystems.map(mapExternalSystem),
    // Actors / decisions / flows / ops notes don't need shape changes — their
    // deprecated fields stay populated and the renderer migration in Phase 5
    // will switch to canonical reads. Initialise `questions` so consumers can
    // safely `arch.questions ?? []` or read directly.
    questions: arch.questions ?? [],
  };
}
